import { bleedPage, contactWrap, FlowTemplate, levelBar, padHorizontal, photoWidget, sectionIcon, type RenderContext, type Zone } from "../base";
import { box, col, expanded, hspace, keep, linkText, padding, rect, rich, row, sized, text, vspace, wrapBox, type Node, type PageSpec } from "../ir";
import type { RenderEntry, RenderSection } from "../model";
import { hex, Pal, WHITE } from "../theme";

const PAD = 46;

/**
 * Horizon — a full-bleed colour banner across the top, then a single airy
 * column with square-marker headings. (horizon_template.dart)
 */
export class HorizonTemplate extends FlowTemplate {
  readonly info = {
    id: "horizon",
    name: "Horizon",
    tagline: "Full-width colour banner that makes an entrance",
    category: "creative" as const,
    layout: "Banner",
    defaultAccent: 0xff2563eb,
    atsFriendly: true,
    supportsPhoto: true,
  };

  readonly fonts = {
    body: "lato" as const,
    heading: "montserrat" as const,
    display: "montserrat" as const,
    headingFollowsBody: false,
    displayFollowsBody: false,
  };

  zoneWidth(c: RenderContext): number {
    return c.format.width - PAD * 2;
  }

  heading(c: RenderContext, s: RenderSection): Node {
    const t = c.t;
    return row([
      sectionIcon(c, s, t.accent, t.fs(11)) ?? rect({ width: 7, height: 7, color: t.accent }),
      hspace(8),
      text(s.title.toUpperCase(), t.style(t.heading.bold, 10, { color: t.ink, letterSpacing: 1.5, lineSpacing: 0 })),
      hspace(10),
      expanded(rect({ height: 0.6, color: t.line })),
    ]);
  }

  entryHeader(c: RenderContext, e: RenderEntry): Node {
    const t = c.t;
    const title = e.title || e.subtitle;
    const subtitle = e.title ? e.subtitle : "";
    return col([
      row(
        [
          expanded(linkText(title, t.style(t.heading.semiBold, 10.2, { color: t.ink, lineSpacing: 0 }), e.url)),
          e.dates && text(e.dates, t.style(t.heading.semiBold, 8.2, { color: t.accentText, lineSpacing: 0 })),
        ],
        { align: "start" }
      ),
      (subtitle || e.meta) &&
        padding(
          { top: 2 },
          rich([
            subtitle ? [subtitle, t.style(t.body.bold, 9.2, { color: t.text })] : null,
            subtitle && e.meta ? ["   ", t.style(t.body.regular, 8.4)] : null,
            e.meta ? [e.meta, t.style(t.body.regular, 8.4, { color: t.muted })] : null,
          ])
        ),
    ]);
  }

  skillsContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    if (!c.showSkillLevels) return super.skillsContent(c, s, z);
    // Two neat columns of bars.
    const t = c.t;
    const width = Math.floor(((c.format.width - PAD * 2 - 24) / 2) * 100) / 100;
    return [
      wrapBox(
        s.skills.map((skill) =>
          sized(
            width,
            undefined,
            col([
              row([
                expanded(text(skill.name, t.style(t.body.regular, 8.8, { color: t.text, lineSpacing: 0 }))),
                text(skill.levelLabel, t.style(t.body.regular, 7.4, { color: t.faint, lineSpacing: 0 })),
              ]),
              vspace(2.5),
              levelBar(skill.level, { color: t.accent, track: t.accentTint, height: 3.2 }),
            ])
          )
        ),
        { spacing: 24, runSpacing: t.gap(6), cross: "start" }
      ),
    ];
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const band = Pal.deep(t.accent);
    const soft = Pal.mix(band, WHITE, 0.8);
    const photo = photoWidget(c, 76, { ring: Pal.mix(band, WHITE, 0.85), ringWidth: 2.5 });

    const banner = box({ bg: hex(band), pad: [34, PAD, 28, PAD], fill: true }, [
      row([
        expanded(
          col([
            m.name &&
              text(m.name.toUpperCase(), t.style(t.display.extraBold, 25, { color: WHITE, letterSpacing: 1.2, lineSpacing: 0 })),
            m.headline && vspace(5),
            m.headline && text(m.headline, t.style(t.body.regular, 11.5, { color: soft, lineSpacing: 0 })),
            m.contacts.length > 0 && vspace(13),
            m.contacts.length > 0 &&
              contactWrap(c, {
                style: t.style(t.body.regular, 8.5, { color: WHITE, lineSpacing: 0 }),
                iconColor: soft,
                spacing: 14,
              }),
          ])
        ),
        photo && hspace(20),
        photo,
      ]),
    ]);

    return bleedPage(c, {
      top: 38,
      firstTop: 0,
      bottom: 30,
      footerRight: PAD,
      footerBottom: 12,
      body: [
        keep([banner, vspace(t.gap(24))], true),
        ...this.allSections(c, m.sections).map((b) => padHorizontal(b, PAD, PAD)),
      ],
    });
  }
}
